import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Typography, Box, Paper, Button,
  TextField, Chip, Alert, Divider, Grid,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const API = process.env.REACT_APP_API_URL;
const P_CUST_ID_CLIENTE = '9c1e80f75ab40c03bc0402e71f2ebdd3';

export default function Suscripcion() {
  const { usuario } = useAuth();
  const [suscripcion, setSuscripcion] = useState(null);
  const [ciclo, setCiclo] = useState('mensual');
  const [cantidadUsuarios, setCantidadUsuarios] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const precioMensual = 2.99;
  const precioAnual = 1.99;
  const precio = ciclo === 'mensual' ? precioMensual : precioAnual;
  const total = ciclo === 'mensual'
    ? (cantidadUsuarios * precioMensual).toFixed(2)
    : (cantidadUsuarios * precioAnual * 12).toFixed(2);

  // Cargar script ePayco
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.epayco.co/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const cargarSuscripcion = () => {
    if (!usuario?.empresa_id) return;
    axios.get(`${API}/api/suscripcion/${usuario.empresa_id}`)
      .then(r => setSuscripcion(r.data))
      .catch(() => setSuscripcion(null));
  };

  useEffect(() => { cargarSuscripcion(); }, []);

  const getDiasRestantes = () => {
    if (!suscripcion) return 0;
    const fecha = suscripcion.plan === 'trial'
      ? suscripcion.trial_hasta
      : suscripcion.suscripcion_hasta;
    if (!fecha) return 0;
    const diff = new Date(fecha) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handlePagar = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/suscripcion/checkout`, {
        empresa_id: usuario.empresa_id,
        cantidad_usuarios: cantidadUsuarios,
        ciclo
      });
      const d = res.data;

      // Verificar que ePayco está cargado
      if (!window.ePayco) {
        setError('Error cargando ePayco. Recarga la página e intenta de nuevo.');
        setLoading(false);
        return;
      }

      const handler = window.ePayco.checkout.configure({
        key: '9c1e80f75ab40c03bc0402e71f2ebdd3',
        test: false
        });

      handler.open({
      name: 'Smart Track',
      description: d.p_description,
      invoice: d.p_id_invoice,
      currency: 'usd',
      amount: String(d.p_amount),
      tax_base: '0',
      tax: '0',
      country: 'co',
      lang: 'es',
      external: 'false',
      response: `https://app.geo-energy-tech.com/pago/resultado`,
      confirmation: `https://app.geo-energy-tech.com/api/suscripcion/confirmacion`,
      extra1: String(usuario.empresa_id),
      extra2: String(cantidadUsuarios),
      extra3: ciclo
    });

    } catch (err) {
      setError('Error al iniciar el pago. Intenta de nuevo.');
      console.error(err);
    }
    setLoading(false);
  };

  const diasRestantes = getDiasRestantes();

  return (
    <Container sx={{ mt: 4, maxWidth: 800 }}>
      <Typography
        variant="h4"
        fontFamily="'Barlow Condensed', sans-serif"
        fontWeight={800}
        textTransform="uppercase"
        gutterBottom
      >
        Suscripción
      </Typography>

      {/* Estado actual */}
      {suscripcion && (
        <Paper sx={{
          p: 3, mb: 4,
          border: '1px solid',
          borderColor: suscripcion.activa ? 'success.main' : 'error.main'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Plan {suscripcion.plan === 'trial' ? 'Prueba Gratuita' : 'Básico'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {suscripcion.plan === 'trial'
                  ? `${diasRestantes} días de prueba restantes`
                  : `Vence en ${diasRestantes} días`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {suscripcion.plan !== 'trial' && (
                <Typography variant="body2" color="text.secondary">
                  {suscripcion.usuarios_pagados} usuario(s) activos
                </Typography>
              )}
              <Chip
                label={suscripcion.activa ? 'Activa' : 'Vencida'}
                color={suscripcion.activa ? 'success' : 'error'}
                size="small"
              />
            </Box>
          </Box>
          {diasRestantes <= 5 && suscripcion.activa && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Tu suscripción vence pronto. Renueva para no perder acceso.
            </Alert>
          )}
          {!suscripcion.activa && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Tu suscripción ha vencido. Activa un plan para continuar usando Smart Track.
            </Alert>
          )}
        </Paper>
      )}

      {/* Plan Básico */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Chip label="Plan Básico" color="primary" sx={{ mb: 1 }} />
            <Typography variant="h3" fontWeight="bold">
              ${precio}
              <Typography component="span" variant="body1" color="text.secondary">
                /usuario/{ciclo === 'mensual' ? 'mes' : 'mes (anual)'}
              </Typography>
            </Typography>
            {ciclo === 'anual' && (
              <Chip label="Ahorra 33%" color="success" size="small" sx={{ mt: 0.5 }} />
            )}
          </Box>
          <ToggleButtonGroup
            value={ciclo}
            exclusive
            onChange={(e, v) => v && setCiclo(v)}
            size="small"
          >
            <ToggleButton value="mensual">Mensual</ToggleButton>
            <ToggleButton value="anual">Anual</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Features */}
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {[
            'Proyectos y Tareas ilimitados',
            'Timesheet y control de horas',
            'Gestión de Clientes',
            'Reportes CSV',
            'Dashboard con gráficas',
            'Soporte por email',
            'Multi-empresa',
            'Acceso por roles'
          ].map(f => (
            <Grid item xs={12} sm={6} key={f}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2">{f}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 3 }} />

        {/* Cantidad usuarios */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Número de usuarios
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              type="number"
              value={cantidadUsuarios}
              onChange={e => setCantidadUsuarios(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: 100 }}
              size="small"
              sx={{ width: 100 }}
            />
            <Typography variant="body2" color="text.secondary">
              usuarios × ${precio} = <strong>${total} USD/{ciclo === 'mensual' ? 'mes' : 'año'}</strong>
            </Typography>
          </Box>
        </Box>

        {/* Trial info */}
        <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mb: 3 }}>
          14 días de prueba gratuita incluidos. No se cobra hasta que venza el período de prueba.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Botón pagar */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={<CreditCardIcon />}
          onClick={handlePagar}
          disabled={loading}
          sx={{ py: 1.5 }}
        >
          {loading ? 'Procesando...' : `Suscribirse — $${total} USD/${ciclo === 'mensual' ? 'mes' : 'año'}`}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
          Pago seguro procesado por ePayco. Cancela cuando quieras.
        </Typography>
      </Paper>
    </Container>
  );
}